import React, { useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import { ArrowUpDown, ArrowUp, ArrowDown, FileSearch } from "lucide-react";
import CfLoader from "../CfLoader/CfLoader";
import { useTranslation } from "react-i18next";
import "./DataTable.css";
import { automationTestIds } from "../../testing/automationTestIds";

const Shimmer = ({ className = "" }) => (
  <div className={`cf-shimmer rounded-lg ${className}`} aria-hidden="true" />
);

const DataTableSkeleton = ({
  columns = [],
  columnCount = 6,
  rowCount = 5,
  dataTestId,
  selectable = false,
  showPagination = true,
  align = "left",
}) => {
  const resolvedColumns =
    columns.length > 0
      ? columns
      : Array.from({ length: Math.max(1, columnCount) }, (_, index) => ({
          key: `col-${index}`,
          label: "",
        }));
  const skeletonCols = [
    ...(selectable ? [{ key: "_select", label: "" }] : []),
    ...resolvedColumns,
  ];
  const isCenterAligned = align === "center";
  const headerAlignClass = isCenterAligned ? "text-center" : "text-left";
  const cellAlignClass = isCenterAligned ? "text-center" : "text-left";
  const cellContentClass = isCenterAligned
    ? "inline-flex items-center justify-center max-w-full"
    : "inline-flex items-center max-w-full";

  const getSkeletonCell = (columnKey, colIndex, totalCols) => {
    if (columnKey === "_select") {
      return <Shimmer className="h-4 w-4 rounded" />;
    }
    if (columnKey === "actions") {
      return (
        <div className="inline-flex items-center justify-center gap-2">
          <Shimmer className="h-9 w-9 rounded-lg" />
          <Shimmer className="h-9 w-9 rounded-lg" />
        </div>
      );
    }
    if (columnKey === "credential_id") {
      return <Shimmer className="h-6 w-28 max-w-full rounded-md" />;
    }
    if (columnKey === "issued_on" || columnKey === "issued_at") {
      return <Shimmer className="h-4 w-24 max-w-full" />;
    }
    if (
      columnKey === "holder" ||
      columnKey === "credential" ||
      columnKey === "holder_name" ||
      columnKey === "credential_name"
    ) {
      return (
        <div className="inline-flex items-center gap-3">
          <Shimmer className="h-9 w-9 rounded-full shrink-0" />
          <div className="flex flex-col gap-1.5 items-start">
            <Shimmer className="h-4 w-28 max-w-full" />
            <Shimmer className="h-3 w-20 max-w-full" />
          </div>
        </div>
      );
    }

    const widthClass =
      colIndex % 3 === 0 ? "w-36" : colIndex % 3 === 1 ? "w-28" : "w-32";

    return (
      <Shimmer
        className={`h-4 max-w-full ${
          colIndex === totalCols - 1 ? "w-16" : widthClass
        }`}
      />
    );
  };

  return (
    <div
      id={dataTestId}
      className="rounded-md overflow-hidden border border-gray-200 bg-white shadow-sm"
      aria-busy="true"
      aria-live="polite"
      data-align={align}
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse table-auto">
          <thead className="bg-cfTableHeaderBackgroundColor">
            <tr>
              {skeletonCols.map((column) => (
                <th
                  key={`skeleton-header-${column.key}`}
                  className={`py-[18px] px-4 border-b border-gray-200 ${headerAlignClass}`}
                >
                  {column.label ? (
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                      {column.label}
                    </span>
                  ) : (
                    <span className={cellContentClass}>
                      <Shimmer className="h-3 w-20 max-w-full rounded" />
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: Math.max(1, rowCount) }).map(
              (_, rowIndex) => (
                <tr
                  key={`skeleton-row-${rowIndex}`}
                  className="border-b border-gray-100 last:border-b-0"
                >
                  {skeletonCols.map((column, colIndex) => (
                    <td
                      key={`skeleton-cell-${rowIndex}-${column.key}`}
                      className={`py-3.5 px-4 ${cellAlignClass}`}
                    >
                      <span className={cellContentClass}>
                        {getSkeletonCell(
                          column.key,
                          colIndex,
                          skeletonCols.length,
                        )}
                      </span>
                    </td>
                  ))}
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
      {showPagination && (
        <div className="flex items-center justify-between py-3 px-[1.2rem] w-full border-t border-gray-200">
          <Shimmer className="h-9 w-36 rounded-md" />
          <Shimmer className="h-9 w-36 rounded-md" />
        </div>
      )}
    </div>
  );
};

const DataTable = ({
  columns: columnDefs = [],
  data = [],
  loading = false,
  emptyMessage = "",
  selectable = false,
  selectedRows = [],
  onRowSelect,
  onSelectAll,
  rowKey = "",
  statusConfig = {},
  dataTestId = automationTestIds.dataTable,
  // totalCount = 0,
  // currentOffset = 0,
  // limit = 8,
  hasNext = false,
  hasPrevious = false,
  onNext,
  onPrevious,
  // onPageClick,
  align = "left",
  showPagination = true,
  emptyHint,
  loadingVariant = "spinner",
  skeletonRowCount = 5,
  onRowClick,
}) => {
  //! Can be used in future to get the current page number
  // const totalPages = Math.ceil(totalCount / limit);
  // const currentPage = Math.floor(currentOffset / limit) + 1;
  const { t } = useTranslation();

  const allSelected =
    data.length > 0 && data.every((row) => selectedRows.includes(row[rowKey]));
  const isCenterAligned = align === "center";
  const headerAlignClass = isCenterAligned ? "text-center" : "text-left";
  const headerContentClass = isCenterAligned
    ? "flex items-center justify-center gap-1.5"
    : "flex items-center gap-1.5";
  const cellAlignClass = isCenterAligned ? "text-center" : "text-left";

  const renderStatusBadge = (value) => {
    const config = statusConfig[value];
    if (!config) return <span>{value}</span>;
    return (
      <span
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap"
        style={{ backgroundColor: config.bgColor, color: config.textColor }}
      >
        <span
          className="cf-status-dot"
          style={{ backgroundColor: config.dotColor }}
        />
        {t(config.label) || value}
      </span>
    );
  };

  const tanstackColumns = useMemo(() => {
    const cols = [];

    // Checkbox column
    if (selectable) {
      cols.push({
        id: "_select",
        header: () => (
          <input
            type="checkbox"
            className="cf-data-table-checkbox"
            aria-label="Select all rows"
            id={automationTestIds.dataTableSelectAll}
            checked={allSelected}
            onChange={() => onSelectAll && onSelectAll(!allSelected)}
          />
        ),
        cell: ({ row }) => {
          const rowId = row.original[rowKey];
          const isSelected = selectedRows.includes(rowId);
          return (
            <input
              type="checkbox"
              className="cf-data-table-checkbox"
              aria-label={`Select row ${rowId}`}
              id={`cf-data-table-select-row-${rowId}`}
              checked={isSelected}
              onChange={() => onRowSelect && onRowSelect(rowId, !isSelected)}
            />
          );
        },
        enableSorting: false,
        size: 48,
      });
    }

    columnDefs.forEach((col) => {
      cols.push({
        id: col.key,
        accessorKey: col.key,
        header: col.label,
        enableSorting: col.sortable !== false,
        cell: (info) => {
          if (col.render) {
            return col.render(info.getValue(), info.row.original);
          }
          if (col.key === "status") {
            return renderStatusBadge(info.getValue());
          }
          const val = info.getValue();
          if (val === undefined || val === null || val === "") {
            return t("workspace:not_applicable");
          }
          return val;
        },
      });
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnDefs, selectable, allSelected, selectedRows, rowKey]);

  const table = useReactTable({
    data,
    columns: tanstackColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: rowKey ? (row) => row[rowKey] : undefined,
  });

  //! Can be used in future to get the current page number
  // const renderPageNumbers = () => {
  //   if (totalPages <= 1) return null;
  //   const pages = [];
  //   const maxVisible = 5;
  //   let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  //   const end = Math.min(totalPages, start + maxVisible - 1);
  //   if (end - start + 1 < maxVisible) {
  //     start = Math.max(1, end - maxVisible + 1);
  //   }
  //   for (let i = start; i <= end; i++) {
  //     pages.push(
  //       <button
  //         key={i}
  //         className={`cf-pagination-page ${i === currentPage ? "active" : ""}`}
  //         onClick={() => onPageClick && onPageClick(i)}
  //       >
  //         {i}
  //       </button>,
  //     );
  //   }
  //   return pages;
  // };

  if (loading) {
    if (loadingVariant === "skeleton") {
      return (
        <DataTableSkeleton
          columns={columnDefs}
          columnCount={columnDefs.length}
          rowCount={skeletonRowCount}
          dataTestId={dataTestId}
          selectable={selectable}
          showPagination={showPagination}
          align={align}
        />
      );
    }

    return (
      <div
        id={dataTestId}
        className="rounded-xl overflow-hidden border border-gray-200 bg-white shadow-sm"
      >
        <div className="flex items-center justify-center min-h-[320px]">
          <CfLoader />
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    const resolvedEmptyHint =
      emptyHint === undefined ? t("workspace:try_adjusting_search") : emptyHint;

    return (
      <div
        id={dataTestId}
        className="rounded-md overflow-hidden border border-gray-200 bg-white shadow-sm"
      >
        <div
          id={`${dataTestId}-empty`}
          className="flex flex-col items-center justify-center py-20 px-6"
        >
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-gray-50 mb-3">
            <FileSearch size={36} strokeWidth={1.5} className="text-gray-300" />
          </div>
          <p
            className="text-base font-semibold text-gray-700 mb-1"
            id={`${dataTestId}-empty-message`}
          >
            {emptyMessage}
          </p>
          {resolvedEmptyHint ? (
            <p className="text-sm text-gray-400">{resolvedEmptyHint}</p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      id={dataTestId}
      className="rounded-md overflow-hidden border border-gray-200 bg-white shadow-sm"
    >
      <div className="overflow-x-auto">
        <table className="w-full border-collapse table-auto">
          <thead className="bg-cfTableHeaderBackgroundColor">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  const sortDirection =
                    sorted === "asc"
                      ? "ascending"
                      : sorted === "desc"
                        ? "descending"
                        : "none";
                  return (
                    <th
                      key={header.id}
                      id={`${dataTestId}-header-${header.id}`}
                      className={`py-[18px] px-5 text-xs font-semibold uppercase tracking-wider text-gray-700 whitespace-nowrap border-b border-gray-200 select-none ${headerAlignClass}`}
                      aria-sort={canSort ? sortDirection : undefined}
                      style={
                        header.id === "_select" ? { width: 48 } : undefined
                      }
                    >
                      <div className={headerContentClass}>
                        {header.isPlaceholder ? null : canSort ? (
                          <button
                            type="button"
                            className={
                              isCenterAligned
                                ? "relative inline-flex items-center justify-center uppercase hover:text-cfPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 rounded-sm"
                                : "inline-flex items-center gap-1.5 uppercase hover:text-cfPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 rounded-sm"
                            }
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <span>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                            </span>
                            <span
                              className={
                                isCenterAligned
                                  ? "absolute left-full top-1/2 -translate-y-1/2 ml-1 inline-flex items-center"
                                  : "inline-flex items-center flex-shrink-0"
                              }
                              aria-hidden="true"
                            >
                              {sorted === "asc" ? (
                                <ArrowUp size={14} className="text-cfPrimary" />
                              ) : sorted === "desc" ? (
                                <ArrowDown
                                  size={14}
                                  className="text-cfPrimary"
                                />
                              ) : (
                                <ArrowUpDown
                                  size={14}
                                  className="text-gray-400 opacity-45"
                                />
                              )}
                            </span>
                          </button>
                        ) : (
                          flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                id={`${dataTestId}-row-${row.id}`}
                className={`transition-colors duration-150 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 ${
                  onRowClick ? "cursor-pointer" : ""
                }`}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={
                  onRowClick ? () => onRowClick(row.original) : undefined
                }
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        if (
                          !event.repeat &&
                          (event.key === "Enter" || event.key === " ")
                        ) {
                          event.preventDefault();
                          onRowClick(row.original);
                        }
                      }
                    : undefined
                }
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    id={`${dataTestId}-cell-${row.id}-${cell.column.id}`}
                    className={`py-3.5 px-5 text-sm text-gray-700 whitespace-nowrap ${cellAlignClass}`}
                    onClick={
                      selectable && cell.column.id === "_select"
                        ? (event) => event.stopPropagation()
                        : undefined
                    }
                    onKeyDown={
                      selectable && cell.column.id === "_select"
                        ? (event) => event.stopPropagation()
                        : undefined
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="flex items-center justify-between py-3 px-[1.2rem] w-full border-t border-gray-200">
          <button
            disabled={!hasPrevious}
            onClick={onPrevious}
            id={automationTestIds.dataTablePrev}
            className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
          >
            <span className="flex items-center justify-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-5 h-5 rtl:-scale-x-100"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                />
              </svg>
              <span>{t("workspace:previous")}</span>
            </span>
          </button>

          <button
            disabled={!hasNext}
            onClick={onNext}
            id={automationTestIds.dataTableNext}
            className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
          >
            <span className="flex items-center justify-center gap-2">
              <span>{t("workspace:next")}</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-5 h-5 rtl:-scale-x-100"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                />
              </svg>
            </span>
          </button>
        </div>
      )}
    </div>
  );
};

export default DataTable;
